import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import html2canvas from 'html2canvas';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';
import NiceButton from "../NiceButton";

const ScreenShotCanvas = forwardRef(({selectedItem, setSelectedItem, isImageLoaded}, ref) => {
    const {isTouch} = useSessionConfiguration();
    const [copied, setCopied] = useState(false);
    const copiedTimerRef = useRef(null);

    useImperativeHandle(ref, () => ({
        shareImage,
    }));

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (copiedTimerRef.current) {
                clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);

    // Clear selection, take screenshot, then restore selection.
    const screenshotWithNoSelection = (callback) => {
        const prevSelection = selectedItem;
        setSelectedItem(null);
        setTimeout(() => {
            callback();
            setTimeout(() => setSelectedItem(prevSelection), 0);
        }, 0);
    };

    // Common screenshot logic that calls the handler with the canvas.
    const performScreenshot = (canvasHandler) => {
        screenshotWithNoSelection(() => {
            const element = document.getElementById('image-container');
            html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
            }).then((canvas) => canvasHandler(canvas));
        });
    };

    const exportAsImage = () => {
        performScreenshot((canvas) => {
            const fullHdCanvas = createFullHdCanvas(canvas);
            const link = document.createElement('a');
            link.download = 'image.png';
            link.href = fullHdCanvas.toDataURL('image/png');
            link.click();
        });
    };

    const shareImage = () => {
        performScreenshot((canvas) => {
            canvas.toBlob((blob) => {
                if (!blob) return;
                const file = new File([blob], 'image.png', {type: blob.type});
                if (navigator.canShare && navigator.canShare({files: [file]})) {
                    navigator.share({
                        files: [file],
                        title: 'Flextagram Flex',
                        text: 'insertUrlHere',
                    })
                        .then(() => console.log('Share successful'))
                        .catch((err) => console.error('Error sharing:', err));
                } else {
                    console.log('Sharing not supported on this device.');
                }
            }, 'image/png');
        });
    };

    const copyToClipboard = () => {
        performScreenshot((canvas) => {
            const fullHdCanvas = createFullHdCanvas(canvas);
            fullHdCanvas.toBlob((blob) => {
                if (!blob) {
                    console.error('Blob conversion failed.');
                    return;
                }
                const item = new ClipboardItem({'image/png': blob});
                navigator.clipboard.write([item])
                    .then(() => {
                        // show transient label for 1 second
                        setCopied(true);
                        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
                        copiedTimerRef.current = setTimeout(() => setCopied(false), 1000);
                        console.log('Image copied to clipboard!');
                    })
                    .catch((err) => console.error('Error copying image to clipboard:', err));
            }, 'image/png');
        });
    };

    function createFullHdCanvas(canvas) {
        const originalWidth = canvas.width;
        const originalHeight = canvas.height;
        const ratio = originalWidth / originalHeight;
        let targetWidth, targetHeight;

        if (ratio >= 1) {
            targetWidth = 1080 * ratio;
            if (targetWidth > 1920) {
                targetWidth = 1920;
                targetHeight = 1920 / ratio;
            } else {
                targetHeight = 1080;
            }
        } else {
            targetHeight = 1080 / ratio;
            if (targetHeight > 1920) {
                targetHeight = 1920;
                targetWidth = 1920 * ratio;
            } else {
                targetWidth = 1080;
            }
        }

        const fullHdCanvas = document.createElement('canvas');
        fullHdCanvas.width = Math.round(targetWidth);
        fullHdCanvas.height = Math.round(targetHeight);
        const ctx = fullHdCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, originalWidth, originalHeight,
            0, 0, fullHdCanvas.width, fullHdCanvas.height);

        return fullHdCanvas;
    }

    return (
        <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingBottom: '12px'}}>
            {isTouch && (
                <NiceButton text={'Share'} onClick={shareImage} showArrowShareIcon={true} disabled={!isImageLoaded}/>
            )}

            {/* Wrap copy button in a relative container so the label can be absolutely positioned near it */}
            <div style={{position: 'relative', display: 'inline-block'}}>
                <NiceButton text={'Copy'} onClick={copyToClipboard} showClipboardIcon={true} disabled={!isImageLoaded}/>
                {copied && (
                    <span style={{
                        position: 'absolute',
                        top: 36, // pixels above the button
                        left: '70%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.75)',
                        color: '#fff',
                        padding: '6px 8px',
                        borderRadius: 4,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                    }}>
                        Copied to clipboard
                    </span>
                )}
            </div>

            <NiceButton text={'Download'} onClick={exportAsImage} showDownloadIcon={true} disabled={!isImageLoaded}/>
            {!isTouch && (
                <NiceButton text={'Share'} onClick={shareImage} showArrowShareIcon={true} disabled={!isImageLoaded}/>
            )}
        </div>
    );
});

export default ScreenShotCanvas;
